
import { Component, Vue } from 'vue-property-decorator';
import moment from 'moment';
import { RepositoryFactory } from '@/api/RepositoryFactory';
import StatisticsRepository from '@/api/repositories/StatisticsRepository';

const statisticsRepository: StatisticsRepository = RepositoryFactory.get('statistics');

const REFRESH_INTERVAL_MS = 30000;

interface LiveSnapshot {
    openRequests: number,
    offersInFlight: number,
    ridesInProgress: number
}

interface WindowedCounts {
    requests: number,
    ridesCreated: number,
    pickups: number,
    completions: number,
    cancellations: { customer: number, driver: number, system: number, total: number }
}

@Component({
    components: {
        InfoTooltipComponent: () => import(
            /* webpackChunkName: "InfoTooltipComponent" */
            '@/components/InfoTooltip.component.vue')
    }
})
export default class LiveCountsView extends Vue {
    public isLoading: boolean = false;
    public lastUpdated: string = '';
    public selectedWindow: string = '24h';

    public live: LiveSnapshot = { openRequests: 0, offersInFlight: 0, ridesInProgress: 0 };
    public counts: WindowedCounts = {
        requests: 0,
        ridesCreated: 0,
        pickups: 0,
        completions: 0,
        cancellations: { customer: 0, driver: 0, system: 0, total: 0 }
    };

    /**
     * Identifies the most recent request; a stale response (window switched
     * mid-flight, or a slow poll racing a manual refresh) is discarded.
     */
    private loadToken: number = 0;

    private refreshTimer: number | null = null;

    /**
     * Kept as a bound instance function so removeEventListener gets the same
     * reference addEventListener registered.
     */
    private onVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
            this.load(true);
        }
    };

    public get windowOptions() {
        return [
            { value: 'today', text: this.$t('LIVE_COUNTS.WINDOWS.TODAY').toString() },
            { value: '12h', text: this.$t('LIVE_COUNTS.WINDOWS.H12').toString() },
            { value: '24h', text: this.$t('LIVE_COUNTS.WINDOWS.H24').toString() },
            { value: '3d', text: this.$t('LIVE_COUNTS.WINDOWS.D3').toString() },
            { value: '7d', text: this.$t('LIVE_COUNTS.WINDOWS.D7').toString() }
        ];
    }

    async created() {
        await this.load();
        this.refreshTimer = window.setInterval(() => this.load(true), REFRESH_INTERVAL_MS);
        document.addEventListener('visibilitychange', this.onVisibilityChange);
    }

    beforeDestroy() {
        if (this.refreshTimer !== null) {
            window.clearInterval(this.refreshTimer);
        }
        document.removeEventListener('visibilitychange', this.onVisibilityChange);
    }

    /**
     * @param auto true for timer/visibility-triggered refreshes: those skip
     * entirely while the tab is hidden (pause-when-hidden) and fail silently
     * rather than toasting every 30 seconds if the backend is unreachable.
     */
    public async load(auto: boolean = false) {
        if (auto && document.visibilityState === 'hidden') {
            return;
        }
        const token = ++this.loadToken;
        try {
            this.isLoading = true;
            const response = await statisticsRepository.getCounts(this.selectedWindow);
            if (token !== this.loadToken) return;
            this.counts = response.data.counts;
            this.live = response.data.live;
            this.lastUpdated = moment().format('HH:mm:ss');
        } catch (e) {
            if (token !== this.loadToken) return;
            if (!auto) {
                Vue.notifyErrorSimplified('LIVE_COUNTS.ERROR');
            }
        } finally {
            if (token === this.loadToken) this.isLoading = false;
        }
    }
}
